// TP-Friendlies composer logic — pure, so the validation + payload mapping (which
// must mirror the M1 backend rules) is unit-tested directly. The component is a thin
// shell over this.
import { serializeMultiTier, parseMultiTier, multiTierDisplay } from "./TierSelector";

export const TEAM_CATEGORIES = [
  { value: "first_team", label: "First team" },
  { value: "first_team_youth", label: "First team + Youth" },
  { value: "youth_2nd", label: "Youth · 2nd team" },
];

export const LOCATION_MODES = [
  { value: "host", label: "Host at our ground", hint: "at our ground" },
  { value: "travel", label: "We travel", hint: "up to a max distance" },
  { value: "neutral", label: "Neutral venue", hint: "agree on a place in chat" },
  { value: "camp", label: "Training camp", hint: "destination + window" },
];

export const emptyComposer = () => ({
  team_category: "first_team",
  team_label: "",
  when_mode: "fixed", // UI: fixed | range (non-camp only)
  date_start: "",
  date_end: "",
  kickoff_time: "",
  location_mode: "host",
  max_distance_km: "",
  camp_country_id: "",
  camp_country_name: "",
  camp_region: "",
  camp_window_start: "",
  camp_window_end: "",
  camp_multiple_matches: false,
  tier: parseMultiTier(""), // { tiers:[], shortcut:null }
  context_line: "",
});

// Map the multi tier value → { tier_mode, tier_min, tier_max } exactly as the BE
// composer expects (range needs min≤max; the shortcuts carry neither).
export const tierPayload = (tierVal) => {
  const v = tierVal || parseMultiTier("");
  if (v.shortcut === "three_or_lower") return { tier_mode: "three_or_lower", tier_min: null, tier_max: null };
  if (!v.tiers.length) return { tier_mode: "any", tier_min: null, tier_max: null };
  return { tier_mode: "range", tier_min: v.tiers[0], tier_max: v.tiers[v.tiers.length - 1] };
};

export const tierPreviewDisplay = (tierVal) => multiTierDisplay(tierVal || parseMultiTier(""));

// Validate the composer state → { valid, errors, payload }. Mirrors friendly_views
// ._validate_and_build: camp needs a window (end≥start); non-camp needs date_start
// (+ date_end>date_start when flexible); a tier range needs min≤max (guaranteed by
// the selector). Camp destination country is required here (the design's whole point
// is that a camp surfaces under its destination).
export const validateComposer = (s) => {
  const errors = {};
  if (!TEAM_CATEGORIES.some((c) => c.value === s.team_category)) errors.team_category = "Pick a team category.";

  const base = {
    team_category: s.team_category,
    team_label: (s.team_label || "").slice(0, 40),
    location_mode: s.location_mode,
    context_line: (s.context_line || "").slice(0, 200),
    ...tierPayload(s.tier),
  };

  let payload;
  if (s.location_mode === "camp") {
    if (!s.camp_country_id) errors.camp_country = "Choose the camp destination country.";
    if (!s.camp_window_start) errors.camp_window_start = "Set the camp start date.";
    if (!s.camp_window_end) errors.camp_window_end = "Set the camp end date.";
    if (s.camp_window_start && s.camp_window_end && s.camp_window_end < s.camp_window_start)
      errors.camp_window_end = "The camp end must be on or after the start.";
    payload = {
      ...base,
      is_flexible: false,
      camp_country_id: s.camp_country_id || null,
      camp_region: (s.camp_region || "").slice(0, 80),
      camp_window_start: s.camp_window_start || null,
      camp_window_end: s.camp_window_end || null,
      camp_multiple_matches: !!s.camp_multiple_matches,
    };
  } else {
    const isFlexible = s.when_mode === "range";
    if (!s.date_start) errors.date_start = "Pick a date.";
    if (isFlexible) {
      if (!s.date_end) errors.date_end = "Pick the end of the range.";
      else if (s.date_end <= s.date_start) errors.date_end = "The end must be after the start.";
    }
    let maxKm = null;
    if (s.location_mode === "travel" && s.max_distance_km !== "" && s.max_distance_km != null) {
      const n = Number(s.max_distance_km);
      if (Number.isNaN(n)) errors.max_distance_km = "Distance must be a number.";
      else maxKm = n;
    }
    payload = {
      ...base,
      is_flexible: isFlexible,
      date_start: s.date_start || null,
      date_end: isFlexible ? s.date_end || null : null,
      kickoff_time: !isFlexible && s.kickoff_time ? s.kickoff_time : null,
      max_distance_km: maxKm,
    };
  }
  return { valid: Object.keys(errors).length === 0, errors, payload };
};

// Repost prefill from a request detail: keep everything, CLEAR the dates (Brief §
// "Repost opens the composer prefilled — dates cleared").
export const prefillFromRequest = (d) => {
  const s = emptyComposer();
  s.team_category = d.team_category || "first_team";
  s.team_label = d.team_label || "";
  s.location_mode = d.location_mode || "host";
  s.max_distance_km = d.max_distance_km ?? "";
  s.camp_country_id = d.camp_country_id || "";
  s.camp_country_name = d.location?.country?.name || "";
  s.camp_region = d.camp_region || "";
  s.camp_multiple_matches = !!d.camp_multiple_matches;
  s.context_line = d.context_line || "";
  s.when_mode = d.is_flexible ? "range" : "fixed";
  // dates cleared on repost:
  s.date_start = "";
  s.date_end = "";
  s.kickoff_time = "";
  s.camp_window_start = "";
  s.camp_window_end = "";
  // tier back to the multi value:
  if (d.tier_mode === "three_or_lower") s.tier = { tiers: [], shortcut: "three_or_lower" };
  else if (d.tier_mode === "range" && d.tier_min) {
    const tiers = [];
    for (let t = d.tier_min; t <= d.tier_max; t += 1) tiers.push(t);
    s.tier = { tiers, shortcut: null };
  } else s.tier = parseMultiTier("");
  return s;
};

export { serializeMultiTier };
